// /plugins: the marketplace catalog. Lists the official index (fetched from
// GitHub raw with a 30 min cache) as cards with risk badges; documentation
// lives under /plugins/docs.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Puzzle, BadgeCheck, BookOpen, GitPullRequest, ExternalLink, ChevronRight } from 'lucide-react';
import { fetchPluginIndex, INDEX_REPO_URL, type PluginIndexEntry } from '../lib/pluginIndex';
import { RiskBadge } from '../components/docs/primitives';

export default function Plugins() {
  const [plugins, setPlugins] = useState<PluginIndexEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPluginIndex()
      // Account-risk (tier C) entries live in the index but are only surfaced
      // in-app. The public page lists the rest and points users into the app
      // (the asterisk note below) for the full catalog.
      .then(ix => setPlugins(ix.plugins.filter(p => p.tier !== 'C')))
      .catch(err => setError(String(err)));
  }, []);

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Plugins</h1>
          <p className="text-textSecondary mt-1 max-w-xl">
            Add-ons for StreamNook from the official index.* Browse and install them from the
            Marketplace inside the app.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/plugins/docs"
            className="glass-button px-3 py-1.5 text-[13px] inline-flex items-center gap-2"
          >
            <BookOpen className="w-3.5 h-3.5" />
            Documentation
          </Link>
          <a
            href={INDEX_REPO_URL}
            target="_blank"
            rel="noreferrer"
            className="glass-button px-3 py-1.5 text-[13px] inline-flex items-center gap-2"
          >
            <GitPullRequest className="w-3.5 h-3.5" />
            Submit a plugin
          </a>
        </div>
      </div>

      {plugins === null && !error && (
        <p className="text-textMuted py-10 text-center">Loading the plugin index…</p>
      )}
      {error && (
        <p className="text-error py-10 text-center">Couldn't reach the plugin index. {error}</p>
      )}

      {plugins && plugins.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          {plugins.map(p => (
            <PluginCard key={p.id} plugin={p} />
          ))}
        </div>
      )}

      {plugins && plugins.length === 0 && <EmptyState />}

      {plugins !== null && (
        <div className="space-y-1.5 text-center text-[12px] text-textMuted">
          <p>
            * This page lists a selection. The full catalog, including plugins we only
            distribute in-app, lives in the Marketplace inside StreamNook.
          </p>
          <p>
            Every listing carries a capability label, shown before you install.{' '}
            <Link to="/plugins/docs/overview#risk-labels" className="text-accent hover:underline">
              What the labels mean
            </Link>
          </p>
        </div>
      )}
    </div>
  );
}

function PluginCard({ plugin }: { plugin: PluginIndexEntry }) {
  const date = plugin.updated_at ?? plugin.released_at;
  return (
    <article className="settings-card flex flex-col gap-3 p-5">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-white/[0.06] bg-white/[0.04]">
          {plugin.icon_url ? (
            <img src={plugin.icon_url} alt="" className="h-full w-full object-cover" />
          ) : (
            <Puzzle className="h-5 w-5 text-textMuted" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h2 className="truncate text-[14px] font-semibold text-textPrimary">{plugin.name}</h2>
            {plugin.author.verified && (
              <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-accent" aria-label="Verified author" />
            )}
          </div>
          <p className="truncate text-[12px] text-textMuted">
            {plugin.author.name} · v{plugin.version}
          </p>
        </div>
        <RiskBadge tier={plugin.tier} />
      </div>
      <p className="flex-1 text-[13px] leading-relaxed text-textSecondary">{plugin.description}</p>
      <div className="flex items-center justify-between text-[12px] text-textMuted">
        <span>
          {typeof plugin.downloads === 'number'
            ? `${plugin.downloads.toLocaleString()} installs`
            : date
              ? `Updated ${new Date(date).toLocaleDateString()}`
              : ''}
        </span>
        {plugin.homepage && (
          <a
            href={plugin.homepage}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 transition-colors hover:text-accent"
          >
            Homepage <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    </article>
  );
}

function EmptyState() {
  return (
    <div className="settings-card p-10">
      <div className="flex flex-col items-center gap-4 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/[0.06] bg-white/[0.04]">
          <Puzzle className="h-7 w-7 text-accent" />
        </div>
        <div>
          <h2 className="text-lg font-semibold">Nothing published yet</h2>
          <p className="mt-1.5 max-w-md text-sm text-textSecondary">
            The official index is open for submissions. Build something, sign it, and open a pull
            request; this page lists every entry the moment it merges.
          </p>
        </div>
        <Link
          to="/plugins/docs/first-plugin"
          className="glass-button mt-1 inline-flex items-center gap-2 px-4 py-2 text-sm"
        >
          Build your first plugin <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
